<template>
  <div v-if="user.isLoggedIn" class="bui-dashboard-page">
    <div class="dash-header">
      <h1 class="dash-welcome" data-testid="dash-welcome">Welcome back, {{ user.fullName }} 👋</h1>
      <p class="dash-subtitle">Where would you like to start?</p>
    </div>

    <div class="dash-primary-cards">
      <template v-for="item in basicDashboardCards" :key="item.name">
        <RouterLink
          v-if="user.hasReadAccessToArea(item.areaAccessName)"
          class="dash-card dash-card--primary"
          :to="user.hasReadAccessToArea(item.areaAccessName) ? item.path : ''"
          :data-testid="`dashboard-link-${item.path.replace(/^\//, '') || 'home'}`"
        >
          <div class="dash-card__icon-wrap">
            <component :is="item.icon" class="dash-card__icon" weight="light" />
          </div>
          <div class="dash-card__title">{{ item.name }}</div>
          <div class="dash-card__desc">{{ item.description }}</div>
          <div class="dash-card__btn">
            {{ item.buttonText }} <PhArrowRight class="dash-card__btn-arrow" weight="bold" />
          </div>
        </RouterLink>
      </template>
    </div>

    <div
      v-if="user.hasReadAccessToArea('Administration')"
      class="dash-admin-cards"
    >
      <template v-for="item in adminDashboardCards" :key="item.name">
        <RouterLink
          v-if="!item.checkIsVisible || item.checkIsVisible(user)"
          class="dash-card dash-card--admin"
          :to="item.path"
          :data-testid="`dashboard-link-${item.path.replace(/^\//, '').replace(/\//g, '-') || 'home'}`"
        >
          <div class="dash-card__icon-wrap dash-card__icon-wrap--sm">
            <component :is="item.icon" class="dash-card__icon" weight="light" />
          </div>
          <div v-if="item.counter" class="dash-card__counter">
            <div class="dash-card__counter-label">{{ item.counter.name }}</div>
            <div class="dash-card__counter-count">{{ unref(item.counter.count) ?? '–' }}</div>
          </div>
          <div class="dash-card__title dash-card__title--sm">{{ item.name }}</div>
          <div class="dash-card__btn dash-card__btn--admin">
            {{ item.buttonText }} <PhArrowRight class="dash-card__btn-arrow" weight="bold" />
          </div>
        </RouterLink>
      </template>
    </div>
  </div>
</template>


<script lang="ts" setup>
import {
  PhImages,
  PhQueue,
  PhCalendarBlank,
  PhMonitorPlay,
  PhClipboardText,
  PhBuildings,
  PhMapTrifold,
  PhUsersThree,
  PhUserGear,
  PhArrowRight,
} from '@phosphor-icons/vue';
import { fetchAuditingRecordCount } from '@/helpers/api/audit/audit';
import useUser from '@/composables/user';
import { onBeforeMount, onMounted, ref, unref } from 'vue';

const user = useUser();
const auditRecords = ref<number | undefined>();

onBeforeMount(() => {
  fetchAuditingRecordCount()
    .then((count: any) => { auditRecords.value = count; })
    .catch((error) => { console.error('Failed to fetch audit fetchAuditingRecordCount:', error); });
});

onMounted(() => {
  user.checkLogin();
});

const basicDashboardCards = [
  {
    name: 'Media',
    areaAccessName: 'MediaLibrary',
    description: 'Upload images and videos.',
    path: '/media',
    icon: PhImages,
    buttonText: 'Upload & Manage',
  },
  {
    name: 'Playlists',
    areaAccessName: 'Playlists',
    description: 'Media is organized into Playlists - then assigned to Campaigns.',
    path: '/playlists',
    icon: PhQueue,
    buttonText: 'Create & Manage',
  },
  {
    name: 'Campaigns',
    areaAccessName: 'Campaigns',
    description: 'Campaigns are collections of Playlists, scheduled to run on specific dates and Players.',
    path: '/campaigns',
    icon: PhCalendarBlank,
    buttonText: 'Create & Manage',
  },
  {
    name: 'Players',
    areaAccessName: 'Players',
    description: "It's showtime! Setup and manage your physical Players.",
    path: '/players',
    icon: PhMonitorPlay,
    buttonText: 'Add & Manage',
  },
];

const adminDashboardCards = [
  {
    name: 'Auditing',
    counter: { name: 'Pending Approval', count: auditRecords },
    path: '/auditing',
    icon: PhClipboardText,
    buttonText: 'Manage',
    checkIsVisible: (user: any) => user.isAuditor(),
  },
  {
    name: 'Organizations',
    path: '/organizations',
    icon: PhBuildings,
    buttonText: 'Manage',
  },
  {
    name: 'Locations',
    path: '/locations',
    icon: PhMapTrifold,
    buttonText: 'Manage',
  },
  {
    name: 'Users',
    path: '/users',
    icon: PhUsersThree,
    buttonText: 'Manage',
  },
  {
    name: 'Roles',
    path: '/roles',
    icon: PhUserGear,
    buttonText: 'Manage',
  },
];
</script>


<style lang="scss" scoped>

.bui-dashboard-page {
  background-color: var(--bui-page-fill, #ff007b);
  flex: 1 1 auto;
  width: 100%;
  min-height: 100vh;
  padding: var(--bui-page-pad-y, 32px) var(--bui-page-pad-x, 48px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @include screen-width-down(768px) {
    padding: 24px 20px;
  }
}

// ─── Header ──────────────────────────────────────────────────────────────────

.dash-header {
  margin-bottom: 22px;
  margin-top: -28px;
}

.dash-welcome {
  margin: 0 0 8px;
  font-family: var(--nav-font-family, 'Robout', system-ui, sans-serif);
  font-weight: 700;
  font-size: clamp(26px, 3vw, 38px);
  color: var(--bui-page-head, #fffcfd);
  letter-spacing: 0.01em;
}

.dash-subtitle {
  margin: 0;
  font-size: 17px;
  font-family: var(--nav-font-family, 'Robout', system-ui, sans-serif);
  font-weight: var(--nav-font-weight, 300);
  color: color-mix(in srgb, var(--bui-page-head, #fffcfd) 70%, transparent);
}

// ─── Shared card shell ───────────────────────────────────────────────────────

.dash-card {
  background: var(--dash-card-fill, rgba(75, 24, 134, 0.45));
  border: 1px solid var(--dash-card-border, rgba(255, 178, 219, 0.15));
  border-radius: var(--dash-card-radius, 16px);
  box-shadow: var(--bui-tbl-card-shadow, 0 8px 32px rgba(75, 24, 134, 0.16));
  color: var(--dash-card-text, #fffcfd);
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
  transition: box-shadow 0.18s ease, transform 0.18s ease;

  &:hover {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.22);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid var(--dash-icon-fg, #fffcfd);
    outline-offset: 3px;
  }
}

// ─── Icon wrapper ─────────────────────────────────────────────────────────────

.dash-card__icon-wrap {
  width: var(--dash-icon-size, 72px);
  height: var(--dash-icon-size, 72px);
  border-radius: 50%;
  background: var(--dash-icon-bg, rgba(164, 32, 200, 0.45));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--dash-icon-fg, #fffcfd);
  flex-shrink: 0;
  margin-bottom: 16px;

  &--sm {
    width: 60px;
    height: 60px;
    margin-bottom: 12px;
  }
}

.dash-card__icon {
  width: 36px;
  height: 36px;

  .dash-card__icon-wrap--sm & {
    width: 30px;
    height: 30px;
  }
}

// ─── Primary cards ────────────────────────────────────────────────────────────

.dash-primary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: var(--bui-page-section-gap, 24px);
  margin-bottom: var(--bui-page-section-gap, 24px);
}

.dash-card--primary {
  padding: var(--dash-card-pad-y, 28px) var(--dash-card-pad-x, 24px);
  justify-content: flex-start;
}

.dash-card__title {
  font-family: var(--nav-font-family, 'Robout', system-ui, sans-serif);
  font-weight: 700;
  font-size: 22px;
  color: var(--dash-card-text, #fffcfd);
  margin-bottom: 10px;

  &--sm {
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 6px;
  }
}

.dash-card__desc {
  font-size: 14px;
  line-height: 1.45;
  color: var(--dash-card-desc, rgba(255, 252, 253, 0.72));
  flex: 1;
  margin-bottom: 20px;
}

.dash-card__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: var(--dash-btn-fill, #a420c8);
  color: var(--dash-btn-text, #fffcfd);
  border: 1px solid var(--dash-btn-border, rgba(255, 178, 219, 0.25));
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: var(--nav-font-family, 'Robout', system-ui, sans-serif);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: opacity 0.15s ease;
  align-self: stretch;
  justify-content: center;
  margin-top: auto;

  &:hover {
    opacity: 0.88;
  }

  &--admin {
    background: transparent;
    border: none;
    padding: 4px 0;
    font-size: 13px;
    color: var(--dash-admin-btn-color, #ffb2db);
    letter-spacing: 0.03em;
    justify-content: center;
    align-self: stretch;
    margin-top: auto;

    &:hover {
      opacity: 0.75;
    }
  }
}

.dash-card__btn-arrow {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

// ─── Admin cards ─────────────────────────────────────────────────────────────

.dash-admin-cards {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--bui-page-section-gap, 24px);
}

.dash-card--admin {
  padding: 24px 20px;
  min-width: 150px;
  max-width: 190px;
  align-items: center;
  flex: 0 0 auto;
  justify-content: space-between;
}

// ─── Counter (Auditing) ───────────────────────────────────────────────────────

.dash-card__counter {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 4px;
}

.dash-card__counter-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--dash-counter-label, rgba(255, 252, 253, 0.65));
}

.dash-card__counter-count {
  font-size: 2.8rem;
  font-weight: 700;
  line-height: 1.1;
  /* Reserve the line box before the async count arrives; the page is
     vertically centered, so a late height change shifts the whole layout. */
  min-height: 1.1em;
  color: var(--dash-counter-color, #baff5c);
}

</style>
